<template>
  <div class="hero-title-box">
    <div class="text-line">
      <!-- 动态显示的文字 -->
      <div
        v-for="(char, index) in displayedText"
        :key="index"
        class="char"
        :class="shouldAnimate && index > 1 ? 'move-text' : ''"
        :ref="`char${index}`"
        :style="
          shouldAnimate && index > 1
            ? `--move-distance: ${highlightWidth}px`
            : ''
        "
        @animationend="onMoveAnimationEnd(index)"
      >
        {{ char }}
      </div>

      <!-- 光标，跟随在新出现的文字后面 -->
      <div v-if="showCursor" class="cursor">|</div>
    </div>

    <!-- 数据智能 div，带边框绘制动画 -->
    <div
      class="data-intelligence"
      ref="dataIntelligenceBox"
      v-if="!showCursor"
      :style="animateMoveEnd ? `left: ${charWidth}px` : ''"
    >
      <svg
        class="border-svg"
        :class="{ 'draw-border': drawBorder }"
        viewBox="0 0 372 106"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient
            id="hero-title-borderGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stop-color="#007bff" />
            <stop offset="100%" stop-color="#00d4ff" />
          </linearGradient>
        </defs>
        <!-- SVG 路径四角用 A 20 20 圆弧  20px的圆角-->
        <path
          d="M 350.5 1.5
             H 21.5
             A 20 20 0 0 0 1.5 21.5
             V 84.5
             A 20 20 0 0 0 21.5 104.5
             H 350.5
             A 20 20 0 0 0 370.5 84.5
             V 21.5
             A 20 20 0 0 0 350.5 1.5
             Z"
          fill="none"
          stroke="url(#hero-title-borderGradient)"
          stroke-width="8"
          stroke-linecap="butt"
          stroke-linejoin="round"
          pathLength="100"
          class="border-path"
        />
      </svg>

      <span class="data-text" :class="{ 'show-text': showDataText }">
        数据智能
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      fullText: '引领时代',
      displayedText: '',
      currentIndex: 0,
      showCursor: true,
      typeSpeed: 100, // 打字速度，毫秒
      blinkDuration: 80, // 光标一次闪烁周期（需与CSS中的animation时长保持一致）
      shouldAnimate: false, // 控制是否开始动画
      highlightWidth: 0, // 存储高亮元素的宽度
      charWidth: 0, // 存储字符的宽度
      animateMoveEnd: false, // 控制是否开始动画
      showDataIntelligence: false, // 控制是否显示数据智能边框动画
      showDataText: false, // 控制是否显示"数据智能"文字
      allAnimationEnd: false, // 控制是否所有动画都结束
      resizeTimer: null, // 用于防抖的定时器
      drawBorder: false, // 使用CSS为data-text绘制边框
    };
  },
  mounted() {
    console.log('组件已挂载');
    // 先让光标闪两下再开始打字（基于 CSS 动画迭代事件，精确计数两次）
    this.$nextTick(() => {
      const cursorEl = this.$el.querySelector('.cursor');
      if (cursorEl) {
        let blinkIterations = 0;
        const handleIter = () => {
          blinkIterations += 1;

          if (blinkIterations >= 2) {
            cursorEl.removeEventListener('animationiteration', handleIter);
            this.startTypewriter();
          }
        };
        cursorEl.addEventListener('animationiteration', handleIter);
      } else {
        this.startTypewriter();
      }
    });

    // 正确绑定resize事件监听器，使用防抖机制
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    // 清理事件监听器
    window.removeEventListener('resize', this.handleResize);
    // 清理定时器
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  },

  methods: {
    handleResize() {
      // 防抖处理，避免频繁触发
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.resetAnimation();
      }, 300); // 300ms防抖延迟
    },

    resetAnimation() {
      // console.log('窗口尺寸变化，重新执行动画');

      // 重置所有动画状态
      this.shouldAnimate = false;
      this.animateMoveEnd = false;
      this.showDataText = false;
      this.drawBorder = false;

      // 重新计算尺寸和位置
      this.$nextTick(() => {
        this.getDataIntelligenceBoxWidth();
        let charWidth = this.getCharWidth(0) * 2 + 7;
        this.charWidth = charWidth;

        // 延迟重新开始动画，确保DOM更新完成
        setTimeout(() => {
          this.shouldAnimate = true;
        }, 100);
      });
    },

    onMoveAnimationEnd(index) {
      if (index === 1) {
        // 当第二个字符动画结束时，开始数据智能边框绘制动画
        this.startBorderAnimation();
      }
    },
    startTypewriter() {
      // 开始打字机效果
      this.typeNextChar();
    },

    typeNextChar() {
      if (this.currentIndex < this.fullText.length) {
        // 添加下一个字符
        this.displayedText += this.fullText[this.currentIndex];
        this.currentIndex++;

        // 继续下一个字符
        setTimeout(() => {
          this.typeNextChar();
        }, this.typeSpeed);
      } else {
        // 所有字符都显示完毕，保持光标闪烁
        // 可以在这里添加其他逻辑，比如隐藏光标或开始新的动画
        console.log('打字完成!');
        this.showCursor = false; // 打字完成后隐藏光标

        // 等 DOM 更新后再获取宽度
        this.$nextTick(() => {
          this.getDataIntelligenceBoxWidth();
          let charWidth = this.getCharWidth(0) * 2 + 7; // 获取第一个字符的宽度
          this.charWidth = charWidth;
        });

        // 1秒后开始动画
        setTimeout(() => {
          this.shouldAnimate = true;
        }, 200);

        // 2秒后开始数据智能边框绘制动画
        // setTimeout(() => {
        //   this.startBorderAnimation();
        // }, 2000);
      }
    },

    getDataIntelligenceBoxWidth() {
      // 确保元素存在
      if (this.$refs.dataIntelligenceBox) {
        const dataIntelligenceBox = this.$refs.dataIntelligenceBox;
        const width = dataIntelligenceBox.offsetWidth;
        const height = dataIntelligenceBox.offsetHeight;
        console.log('highlight 宽度:', width);

        // 将宽度存储到data中
        this.highlightWidth = width + 10;
      } else {
        console.warn('showHighlight 元素不存在');
      }
    },

    getCharWidth(charIndex) {
      // 获取指定字符的宽度
      const charRef = this.$refs[`char${charIndex}`];
      if (charRef && charRef[0]) {
        const charElement = charRef[0];
        const width = charElement.offsetWidth;
        const height = charElement.offsetHeight;

        console.log(
          `字符 ${charIndex} (${this.displayedText[charIndex]}) 宽度:`,
          width,
          'px'
        );

        return width;
      } else {
        console.warn(`字符 ${charIndex} 不存在`);
        return 0;
      }
    },

    onMoveAnimationEnd(index) {
      // 动画结束时触发
      console.log(
        `字符 ${index} 的移动动画结束，移动距离: ${this.highlightWidth}px`
      );

      // 可以在这里执行动画结束后的操作
      if (index === this.displayedText.length - 1) {
        // 最后一个字符的动画结束
        console.log('所有移动动画完成！');
        this.animateMoveEnd = true;
        this.startBorderAnimation();
      }
    },

    startBorderAnimation() {
      console.log('开始边框动画(CSS)');
      // 先绘制边框，完成后再显示文字
      this.showDataText = false;
      this.$nextTick(() => {
        this.drawBorder = true;
        setTimeout(() => {
          this.showDataText = true;
          setTimeout(() => {
            this.$emit('animationComplete');
          }, 600);
        }, 800);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.hero-title-box {
  position: relative;
  width: 750px;
  display: flex;
  align-items: center;
}

.typewriter-container {
  background: rgba(255, 255, 255, 0.95);
  /* padding: 60px 80px; */
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);

  width: 500px;
}

.text-line {
  display: flex;
  align-items: center;
  font-size: 90px;
  font-weight: 600;
  color: #222226;
  font-family: 'MiSans-Demibold';
}

.char {
  margin-right: 2px;
}

.move-text {
  animation: moveRight 0.3s ease-out forwards;
}

@keyframes moveRight {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(var(--move-distance, 100px));
  }
}

/* 数据智能样式 */
.data-intelligence {
  position: absolute;
  top: 0;
  left: 50%;
  // transform: translateX(-50%);
  // padding: 20px 40px;
  border: none;
  border-radius: 20px;
  overflow: hidden;
  width: 372px;
  height: 106px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.data-text {
  font-size: 84px;
  font-weight: 600;
  background: linear-gradient(135deg, #005fe6, #2eb6d8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  opacity: 0;
  transform: translateY(28px);
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  margin-top: 4px;
}
.data-text.show-text {
  opacity: 1;
  transform: translateY(0);
}

/* 纯CSS为 data-text 绘制边框（无SVG） */
.data-text.draw-border::before {
  content: '';
  position: absolute;
  inset: 0; /* 边框绘制在内容周围 */
  border-radius: 20px;
  pointer-events: none;
  /* 使用多重渐变分别作为四条边，配合背景尺寸动画实现依次绘制 */
  background: linear-gradient(90deg, #007bff, #00d4ff) top right / 0 10px
      no-repeat,
    /* 顶部：从右向左 */ linear-gradient(180deg, #007bff, #00d4ff) top left /
      10px 0 no-repeat,
    /* 左侧：从上向下 */ linear-gradient(270deg, #007bff, #00d4ff) bottom left /
      0 10px no-repeat,
    /* 底部：从左向右 */ linear-gradient(0deg, #007bff, #00d4ff) bottom right /
      10px 0 no-repeat; /* 右侧：从下向上 */
  animation: border-draw 1s linear forwards;
}

@keyframes border-draw {
  0% {
    background-size: 0 10px, 10px 0, 0 10px, 10px 0; /* 从右上角起点 */
  }
  25% {
    background-size: 100% 10px, 10px 0, 0 10px, 10px 0; /* 顶部完成，向左绘制 */
  }
  50% {
    background-size: 100% 10px, 10px 100%, 0 10px, 10px 0; /* 左侧完成，向下绘制 */
  }
  75% {
    background-size: 100% 10px, 10px 100%, 100% 10px, 10px 0; /* 底部完成，向右绘制 */
  }
  100% {
    background-size: 100% 10px, 10px 100%, 100% 10px, 10px 100%; /* 右侧完成，向上收尾 */
  }
}

.border-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.border-path {
  stroke-dasharray: 0 100;
  stroke-dashoffset: 0;
}
.border-svg.draw-border .border-path {
  animation: svg-border-draw 1s linear forwards;
}
@keyframes svg-border-draw {
  100% {
    stroke-dasharray: 100 0;
  }
}

.cursor {
  color: #005fe6;
  font-family: auto;
  width: 4px;
  animation: blink 0.3s infinite;
  // margin-left: 4px;
  position: relative;
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}
</style>
